import CheckIcon from "../../assets/images/game-board/check-icon.svg";
import { useTranslation } from "react-i18next";

interface ParticipantStats {
  _id: string;
  full_name: string;
  email: string;
  total_points: number;
  answers_given: number;
  correct_answer_count: number;
  is_guest?: boolean;
  IQ?: number | null;
  IQ_old?: number | null;
}

const getIQChange = (iq?: number | null, iqOld?: number | null) => {
  if (typeof iq !== "number" || typeof iqOld !== "number") return null;
  const delta = iq - iqOld;
  if (delta === 0) return null;
  return { delta, isIncreased: delta > 0 };
};

interface GameResultBoxProps {
  BtnText: string;
  score: string;
  TotalRun: string;
  CurrentRun: string;
  isCorrect?: boolean;
  participants?: ParticipantStats[];
}

const GameResultBox = ({
  BtnText,
  score,
  TotalRun,
  CurrentRun,
  isCorrect = true,
  participants = [],
}: GameResultBoxProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={`2xl:pt-[0.57vw] lg:pt-2.5 pt-1 2xl:rounded-[0.83vw] lg:rounded-2xl rounded-xl text-center 2xl:border-[0.078125vw] border-[1.5px] overflow-hidden px-2 relative ${isCorrect
        ? "bg-primary-yellow border-primary-orange 2xl:shadow-[0px_0.208vw_0px_#D76A4D] shadow-[0px_3px_0px_#D76A4D]"
        : "bg-[#CCB58B] border-black-300"
        }`}>
        {/* Winner Badge */}

        {/* Team Name */}
        <div className="2xl:max-w-full lg:max-w-full max-w-full 2xl:text-[0.9375vw]/none xl:text-lg/none lg:text-base/none text-sm/none font-garamond font-bold landscape:max-lg:text-xs/none capitalize">
          {BtnText}
        </div>

        {/* Score */}
        <h6
          className="text-center font-garamond font-bold text-primary-rose 2xl:text-[1.875vw]/tight xl:text-xl/none text-base/none 2xl:min-h-[2.8125vw] xl:min-h-10 md:min-h-8 p-1 2xl:p-[0.46875vw] flex justify-center items-center md:bg-size-[91%_90%] 2xl:max-w-[9.375vw] max-w-[8rem] w-full mx-auto bg-center bg-no-repeat 2xl:my-[0.416vw] xl:my-1.5 md:my-1 my-0  landscape:max-lg:bg-size-[70%_70%] bg-size-[80%_80%] landscape:max-lg:my-0 landscape:max-lg:py-1 min-h-6 border-1 border-black-300 rounded-md bg-[#C9D863]"
        // style={{ backgroundImage: `url(${GreenBg})` }}
        >
          <span className="pt-1">{score}</span>
        </h6>

        {/* Statistics Section */}
        <div className="2xl:px-[0.5vw] px-2 2xl:mb-[0.5vw] mb-2">
          <div className="flex mb-1">
            <div className="text-center w-[50%]">
              <h5 className="flex 2xl:gap-[0.41vw] gap-2 items-center 2xl:text-[0.93vw]/snug text-lg/snug 2xl:mb-[0.21vw] mb-1 font-semibold font-garamond justify-center">
                <span className="pt-0.5">{TotalRun}</span>
              </h5>
              <span className="block text-center 2xl:text-[0.72vw] text-sm/tight font-normal font-garamond text-black-300">
                {t("gameResultBox.total")}
              </span>
            </div>
            <div className="text-center w-[50%] 2xl:border-l-[0.053vw] ltr:border-l rtl:border-r border-black-400">
              <h5 className="flex 2xl:gap-[0.41vw] gap-2 items-center 2xl:text-[0.93vw]/snug text-lg/snug 2xl:mb-[0.21vw] mb-1 font-semibold font-garamond justify-center">
                <img src={CheckIcon} alt="" className="2xl:w-[0.94vw] w-[18px]" />
                <span className="pt-0.5">{CurrentRun}</span>
              </h5>
              <span className="block text-center 2xl:text-[0.72vw] text-sm/tight font-normal font-garamond text-black-300">
                {t("gameResultBox.correct")}
              </span>
            </div>
          </div>
        </div>
        {/* Player Statistics - always visible */}
        {participants.length > 0 && (
          <div className="2xl:px-[0.5vw] px-2 2xl:pb-[0.7vw] pb-2 border-t border-black-200/40 pt-2 2xl:pt-[0.5vw]">
            <div className="flex items-center justify-between mb-1 2xl:mb-[0.4vw] gap-2">
              <span className="font-garamond font-bold text-black-300 2xl:text-[0.9vw] text-sm/tight">
                {t("gameResultBox.playerStatistics")}
              </span>
              <span className="font-garamond text-black-300 2xl:text-[0.72vw] text-xs/tight">
                {participants.length}{" "}
                {participants.length === 1
                  ? t("gameResultBox.player")
                  : t("gameResultBox.players")}
              </span>
            </div>
            <div className="2xl:space-y-[0.35vw] space-y-1.5">
              {participants
                .slice()
                .sort((a, b) => b.total_points - a.total_points)
                .map((participant, index) => {
                  const iqChange = getIQChange(participant.IQ, participant.IQ_old);
                  return (
                    <div
                      key={participant._id}
                      className="2xl:border-[0.078vw] border border-black-200 bg-primary-ivory/70 2xl:rounded-[0.26vw] rounded-[5px] 2xl:px-[0.5vw] px-2 2xl:py-[0.35vw] py-1"
                    >
                      <div className="flex items-center gap-2.5">
                        {/* Rank badge */}
                        <div
                          className={`2xl:w-[1.7vw] w-6 2xl:h-[1.7vw] h-6 rounded-full flex items-center justify-center font-garamond font-bold 2xl:text-[0.75vw] text-xs shrink-0 ${index === 0
                            ? "bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 text-white"
                            : index === 1
                              ? "bg-[#E7E6E6] text-black-300"
                              : index === 2
                                ? "bg-primary-orange text-white"
                                : "bg-[#E7E6E6] text-black-300"
                            }`}
                        >
                          {index + 1}
                        </div>

                        {/* Player info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-garamond font-bold text-black-300 2xl:text-[0.8vw] text-xs/tight truncate text-start">
                            <span>{participant.full_name}</span>
                          </p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            <div className="flex items-center gap-1">
                              <img src={CheckIcon} alt="" className="2xl:w-[0.65vw] w-3 h-3" />
                              <span className="font-garamond text-black-300 2xl:text-[0.7vw] text-[11px]/snug">
                                {participant.correct_answer_count}{" "}
                                {participant.correct_answer_count === 1
                                  ? t("gameResultBox.correctAnswer")
                                  : t("gameResultBox.correctAnswers")}
                              </span>
                            </div>
                            {!participant?.is_guest && typeof participant.IQ === "number" && (
                              <span className="font-garamond font-extrabold text-black-300 2xl:text-[1vw] md:text-base text-sm inline-flex items-center gap-1">
                                {t("gameResultBox.iqScore")}: {participant.IQ}
                                {iqChange && (
                                  <span
                                    className={`font-garamond 2xl:text-[0.75vw] md:text-xs text-[10px] ${iqChange.isIncreased ? "text-green-700" : "text-red-600"}`}
                                  >
                                    {iqChange.isIncreased ? "↑" : "↓"} {Math.abs(iqChange.delta)}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Points */}
                        <div className="skew-x-[-10deg] 2xl:border-[0.078vw] border border-black-300 bg-primary-orange 2xl:rounded-[0.26vw] rounded-[4px] 2xl:px-[0.6vw] px-2 2xl:py-[0.25vw] py-0.5">
                          <div className="skew-x-[10deg] text-right">
                            <p className="font-garamond font-bold text-white 2xl:text-[0.9vw] text-xs/tight leading-tight">
                              {Math.round(participant.total_points).toLocaleString()}
                            </p>
                            <p className="font-garamond text-white 2xl:text-[0.6vw] text-[9px]">
                              {t("gameResultBox.points")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GameResultBox;
